import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PrivacyModeProvider } from "@/context/PrivacyModeContext";
import { TenantConfigProvider } from "@/context/TenantConfigContext";
import PrivacyPinModal from "@/components/PrivacyPinModal";
import ProtectedRoute from "@/components/ProtectedRoute";
import IdleSignOut from "@/components/auth/IdleSignOut";
import RouteTracker from "@/components/attendance/RouteTracker";
import Layout from "@/components/Layout";
import GlobalLoader from "@/components/GlobalLoader";
import { GuideProvider, isSalesPerson, useGuide } from "@/components/guide/GuideProvider";
import { LostReasonProvider } from "@/components/LostReasonDialog";
import { UI_THEME, UI_SHELL } from "@/lib/featureFlags";

// Login is the only screen every visitor needs before auth, so it stays in
// the main bundle. Everything past it loads on demand — the app was
// shipping all ~27 pages in one bundle regardless of which one you landed
// on, which is most of what made the very first load feel slow.
import Login from "@/pages/Login";
const CommandCentre = lazy(() => import("@/pages/CommandCentre"));
const AccountsHome = lazy(() => import("@/pages/AccountsHome"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Quotes = lazy(() => import("@/pages/Quotes"));
const Sales = lazy(() => import("@/pages/Sales"));
const DeliveryChallans = lazy(() => import("@/pages/DeliveryChallans"));
const Visitors = lazy(() => import("@/pages/Visitors"));
const Calls = lazy(() => import("@/pages/Calls"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const SalesTracker = lazy(() => import("@/pages/SalesTracker"));
const Leads = lazy(() => import("@/pages/Leads"));
const Architects = lazy(() => import("@/pages/Architects"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const InventoryAnalytics = lazy(() => import("@/pages/InventoryAnalytics"));
const PurchaseOrders = lazy(() => import("@/pages/PurchaseOrders"));
const ManufacturerOrders = lazy(() => import("@/pages/ManufacturerOrders"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const DailyPlanner = lazy(() => import("@/pages/DailyPlanner"));
const WorkScheduler = lazy(() => import("@/pages/WorkScheduler"));
const GoodsReceipts = lazy(() => import("@/pages/GoodsReceipts"));
const StockCounts = lazy(() => import("@/pages/StockCounts"));
const Projects = lazy(() => import("@/pages/Projects"));
const Attendance = lazy(() => import("@/pages/Attendance"));
const PayrollPage = lazy(() => import("@/pages/PayrollPage"));
const RoleManager = lazy(() => import("@/pages/RoleManager"));

// Previously orphaned — the sidebar linked to these but no route existed
const Outstanding = lazy(() => import("@/pages/Outstanding"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const PettyCash = lazy(() => import("@/pages/PettyCash"));
const MoneyRequests = lazy(() => import("@/pages/MoneyRequests"));
const ProfitLoss = lazy(() => import("@/pages/ProfitLoss"));
const Cashbook = lazy(() => import("@/pages/Cashbook"));
const TallyLedger = lazy(() => import("@/pages/TallyLedger"));
const Meets = lazy(() => import("@/pages/Meets"));

// Recovered parity modules
const Reports = lazy(() => import("@/pages/Reports"));
const ExecutiveDashboard = lazy(() => import("@/pages/ExecutiveDashboard"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const Discussions = lazy(() => import("@/pages/Discussions"));
const DWSurvey = lazy(() => import("@/pages/DWSurvey"));
const QuoteWorkspace = lazy(() => import("@/pages/QuoteWorkspace"));
const QuoteBuilder = lazy(() => import("@/pages/QuoteBuilder"));
const StoreWalk = lazy(() => import("@/pages/StoreWalk"));
const StockLedger = lazy(() => import("@/pages/StockLedger"));
const DataCentre = lazy(() => import("@/pages/DataCentre"));
const DataHealth = lazy(() => import("@/pages/DataHealth"));
const FinancialYear = lazy(() => import("@/pages/FinancialYear"));
const Workflows = lazy(() => import("@/pages/Workflows"));
const FormBuilder = lazy(() => import("@/pages/FormBuilder"));
const Flows = lazy(() => import("@/pages/Flows"));
const GoLiveData = lazy(() => import("@/pages/GoLiveData"));
const DataCheck = lazy(() => import("@/pages/DataCheck"));
const StartHere = lazy(() => import("@/pages/StartHere"));
const BusinessSettings = lazy(() => import("@/pages/BusinessSettings"));
const MasterData = lazy(() => import("@/pages/MasterData"));
const CustomFields = lazy(() => import("@/pages/CustomFields"));
const Teams = lazy(() => import("@/pages/Teams"));
const RolesPermissions = lazy(() => import("@/pages/RolesPermissions"));

// Operational flow: Customer
const Customers = lazy(() => import("@/pages/Customers"));
const CustomerPage = lazy(() => import("@/pages/CustomerPage"));
const ProjectPage = lazy(() => import("@/pages/ProjectPage"));
const QuoteFollowups = lazy(() => import("@/pages/QuoteFollowups"));
const ProjectPnL = lazy(() => import("@/pages/ProjectPnL"));
const Incentives = lazy(() => import("@/pages/Incentives"));
const Payments = lazy(() => import("@/pages/Payments"));
const AuditTrail = lazy(() => import("@/pages/AuditTrail"));
const RecordChain = lazy(() => import("@/pages/RecordChain"));
const Service = lazy(() => import("@/pages/Service"));
const Catalogues = lazy(() => import("@/pages/Catalogues"));
const VendorBrochures = lazy(() => import("@/pages/VendorBrochures"));
const VirtualCatalogue = lazy(() => import("@/pages/VirtualCatalogue"));
const ArchitectStudio = lazy(() => import("@/pages/ArchitectStudio"));
const Moodboards = lazy(() => import("@/pages/Moodboards"));
const PublicCatalogue = lazy(() => import("@/pages/PublicCatalogue"));
const FollowUps = lazy(() => import("@/pages/FollowUps"));

// Suspense fallback while a route's lazy chunk downloads — same branded
// mark as GlobalLoader (lib/loadingStore drives that one off API calls;
// this one is a static always-on version for the one moment there's no
// request in flight yet to track, just a chunk loading).
function PageLoader() {
  return (
    <div className="madio-loader-overlay" style={{ position: "fixed" }} role="status" aria-live="polite" aria-label="Loading">
      <div className="madio-loader-panel">
        <div className="madio-loader-badge">
          <svg className="madio-loader-ring" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <circle className="madio-loader-ring-track" cx="32" cy="32" r="27" strokeWidth="4" />
            <circle className="madio-loader-ring-sweep" cx="32" cy="32" r="27" strokeWidth="4" />
          </svg>
          <span className="madio-loader-mark">M</span>
        </div>
        <span className="madio-loader-text">MADIO</span>
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}

// Home: the company dashboard, or for the accounts desk (money grants, and no selling or no dashboard) the Accounts
// Desk, so an accounts login opens on what it works on instead of sales numbers it cannot load.
const HOME_PAGES = ["dashboard", "invoice-gen", "cashbook"];
const START_SHOWN = "madio:start-shown";
function Home() {
  const { user, canAccess, rolesReady } = useAuth();
  const guide = useGuide();
  const location = useLocation();
  if (user?.role_id && user.role !== "admin" && !rolesReady) return <PageLoader />;
  // Sales logins open on Start here once per sign-in (docs/GUIDANCE.md); "My dashboard" there comes back here.
  if (isSalesPerson(user, canAccess) && !location.state?.fromStart) {
    if (!guide.ready) return <PageLoader />;
    let shown = true;
    try { shown = sessionStorage.getItem(START_SHOWN) === user.id; } catch { /* storage blocked: don't loop */ }
    if (guide.state.start_on_login && !shown) {
      try { sessionStorage.setItem(START_SHOWN, user.id); } catch { /* ignore */ }
      return <Navigate to="/start" replace />;
    }
  }
  const money = canAccess(["invoice-gen", "cashbook"]);
  const accountsDesk = money && (!canAccess("dashboard") || (!canAccess("leads") && !canAccess("quotes")));
  return accountsDesk ? <AccountsHome /> : <CommandCentre />;
}

function App() {
  // Static per build (UI_THEME comes from a build-time env var, not runtime
  // state), so this only needs to run once — sets the attribute every CSS
  // rule in index.css's [data-ui-theme="light"] block keys off.
  useEffect(() => {
    document.documentElement.setAttribute("data-ui-theme", UI_THEME);
    document.documentElement.setAttribute("data-ui-shell", UI_SHELL);
  }, []);

  return (
    <AuthProvider>
      <IdleSignOut />
      <RouteTracker />
      <TenantConfigProvider>
      <PrivacyModeProvider>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:px-4 focus:py-2 focus:rounded-xl focus:bg-blue-600 focus:text-white focus:outline-none focus:ring-2 focus:ring-white"
      >
        Skip to main content
      </a>
      <Toaster position="top-right" richColors closeButton />
      <GlobalLoader />
      <PrivacyPinModal />
      <LostReasonProvider>
      <BrowserRouter>
        <GuideProvider>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/c/:token" element={<PublicCatalogue />} />
          <Route
            path="/"
            element={
              <ProtectedRoute page={HOME_PAGES} landing>
                <Layout><Home /></Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/accounts" element={<ProtectedRoute page={["invoice-gen", "cashbook"]}><Layout><AccountsHome /></Layout></ProtectedRoute>} />
          <Route
            path="/overview"
            element={
              <ProtectedRoute page="dashboard" landing>
                <Layout><CommandCentre /></Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/pipeline" element={<ProtectedRoute page="pipeline"><Layout><Pipeline /></Layout></ProtectedRoute>} />
          <Route path="/quotes" element={<ProtectedRoute page="quotes"><Layout><Quotes /></Layout></ProtectedRoute>} />
          <Route path="/sales" element={<ProtectedRoute page="sales"><Layout><Sales /></Layout></ProtectedRoute>} />
          <Route path="/delivery-challans" element={<ProtectedRoute page="sales"><Layout><DeliveryChallans /></Layout></ProtectedRoute>} />
          <Route path="/visitors" element={<ProtectedRoute page="visitors"><Layout><Visitors /></Layout></ProtectedRoute>} />
          <Route path="/calls" element={<ProtectedRoute page="calls"><Layout><Calls /></Layout></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute page="analytics"><Layout><Analytics /></Layout></ProtectedRoute>} />
          <Route path="/sales-tracker" element={<ProtectedRoute page="analytics"><Layout><SalesTracker /></Layout></ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute page="leads"><Layout><Leads /></Layout></ProtectedRoute>} />
          <Route path="/architects" element={<ProtectedRoute page="architects"><Layout><Architects /></Layout></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute page="inventory"><Layout><Inventory /></Layout></ProtectedRoute>} />
          <Route path="/inventory/analytics" element={<ProtectedRoute page="inv-analytics"><Layout><InventoryAnalytics /></Layout></ProtectedRoute>} />
          <Route path="/purchase-orders" element={<ProtectedRoute page="purchase-orders"><Layout><PurchaseOrders /></Layout></ProtectedRoute>} />
          <Route path="/manufacturer-orders" element={<ProtectedRoute page="manufacturer-orders"><Layout><ManufacturerOrders /></Layout></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute page="tasks"><Layout><Tasks /></Layout></ProtectedRoute>} />
          <Route path="/daily-planner" element={<ProtectedRoute page="daily-planner"><Layout><DailyPlanner /></Layout></ProtectedRoute>} />
          <Route path="/work-schedule" element={<ProtectedRoute page="tasks"><Layout><WorkScheduler /></Layout></ProtectedRoute>} />
          <Route path="/goods-receipts" element={<ProtectedRoute page="inventory"><Layout><GoodsReceipts /></Layout></ProtectedRoute>} />
          <Route path="/stock-counts" element={<ProtectedRoute page="inventory"><Layout><StockCounts /></Layout></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute page="projects"><Layout><Projects /></Layout></ProtectedRoute>} />
          <Route path="/projects/:id" element={<ProtectedRoute page="projects"><Layout><ProjectPage /></Layout></ProtectedRoute>} />
          <Route path="/catalogues" element={<ProtectedRoute page="quotes"><Layout><Catalogues /></Layout></ProtectedRoute>} />
          <Route path="/architect-studio" element={<ProtectedRoute page={["quotes", "visitors", "leads"]}><Layout><ArchitectStudio /></Layout></ProtectedRoute>} />
          <Route path="/architect-studio/boards" element={<ProtectedRoute page={["quotes", "visitors", "leads"]}><Layout><Moodboards /></Layout></ProtectedRoute>} />
          <Route path="/architect-studio/boards/:id" element={<ProtectedRoute page={["quotes", "visitors", "leads"]}><Layout><Moodboards /></Layout></ProtectedRoute>} />
          <Route path="/virtual-catalogue" element={<ProtectedRoute page={["quotes", "visitors", "leads"]}><Layout><VirtualCatalogue /></Layout></ProtectedRoute>} />
          <Route path="/vendor-brochures" element={<ProtectedRoute page="quotes"><Layout><VendorBrochures /></Layout></ProtectedRoute>} />
          <Route path="/service" element={<ProtectedRoute page="projects"><Layout><Service /></Layout></ProtectedRoute>} />
          <Route path="/follow-ups" element={<ProtectedRoute page="leads"><Layout><FollowUps /></Layout></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute page="attendance"><Layout><Attendance /></Layout></ProtectedRoute>} />
          <Route path="/people/payroll" element={<ProtectedRoute page="payroll"><Layout><PayrollPage /></Layout></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute page="roles"><Layout><RoleManager /></Layout></ProtectedRoute>} />

          {/* Previously orphaned — sidebar linked here but no route existed */}
          <Route path="/outstanding" element={<ProtectedRoute page="outstanding"><Layout><Outstanding /></Layout></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute page="invoice-gen"><Layout><Invoices /></Layout></ProtectedRoute>} />
          <Route path="/petty-cash" element={<ProtectedRoute page="petty"><Layout><PettyCash /></Layout></ProtectedRoute>} />
          <Route path="/money-requests" element={<ProtectedRoute page="expenses"><Layout><MoneyRequests /></Layout></ProtectedRoute>} />
          <Route path="/approvals" element={<ProtectedRoute page="expenses"><Layout><MoneyRequests /></Layout></ProtectedRoute>} />
          <Route path="/finance/pnl" element={<ProtectedRoute page="pnl"><Layout><ProfitLoss /></Layout></ProtectedRoute>} />
          <Route path="/cashbook" element={<ProtectedRoute page="cashbook"><Layout><Cashbook /></Layout></ProtectedRoute>} />
          <Route path="/finance/tally" element={<ProtectedRoute page="cashbook"><Layout><TallyLedger /></Layout></ProtectedRoute>} />
          <Route path="/meets" element={<ProtectedRoute page="meetplan"><Layout><Meets /></Layout></ProtectedRoute>} />

          {/* Recovered parity modules */}
          <Route path="/reports" element={<ProtectedRoute page="reports"><Layout><Reports /></Layout></ProtectedRoute>} />
          <Route path="/executive" element={<ProtectedRoute page="executive"><Layout><ExecutiveDashboard /></Layout></ProtectedRoute>} />
          <Route path="/alerts" element={<ProtectedRoute page="alerts"><Layout><Alerts /></Layout></ProtectedRoute>} />
          <Route path="/discussions" element={<ProtectedRoute page="discussions"><Layout><Discussions /></Layout></ProtectedRoute>} />
          <Route path="/dw-survey" element={<ProtectedRoute page="dwsurvey"><Layout><DWSurvey /></Layout></ProtectedRoute>} />
          <Route path="/quotes/ws/:id" element={<ProtectedRoute page="quotes"><Layout><QuoteWorkspace /></Layout></ProtectedRoute>} />
          <Route path="/quotes/walk" element={<ProtectedRoute page="quotes"><Layout><StoreWalk /></Layout></ProtectedRoute>} />
          <Route path="/quotes/builder" element={<ProtectedRoute page="quote-builder"><Layout><QuoteBuilder /></Layout></ProtectedRoute>} />
          <Route path="/quotes/builder/:id" element={<ProtectedRoute page="quote-builder"><Layout><QuoteBuilder /></Layout></ProtectedRoute>} />
          <Route path="/stock-ledger" element={<ProtectedRoute page="stock-ledger"><Layout><StockLedger /></Layout></ProtectedRoute>} />
          <Route path="/data-centre" element={<ProtectedRoute page="data-centre"><Layout><DataCentre /></Layout></ProtectedRoute>} />
          <Route path="/admin/data-health" element={<ProtectedRoute page="data-health"><Layout><DataHealth /></Layout></ProtectedRoute>} />
          <Route path="/admin/financial-year" element={<ProtectedRoute page="financial-year"><Layout><FinancialYear /></Layout></ProtectedRoute>} />
          <Route path="/admin/workflows" element={<ProtectedRoute page="workflows"><Layout><Workflows /></Layout></ProtectedRoute>} />
          <Route path="/admin/forms" element={<ProtectedRoute page="forms"><Layout><FormBuilder /></Layout></ProtectedRoute>} />
          <Route path="/admin/flows" element={<ProtectedRoute page="flows"><Layout><Flows /></Layout></ProtectedRoute>} />
          <Route path="/admin/go-live" element={<ProtectedRoute page="go-live"><Layout><GoLiveData /></Layout></ProtectedRoute>} />
          {/* Start here (docs/GUIDANCE.md) rides grants people already have, so nobody needs re-permissioning. */}
          <Route path="/start" element={<ProtectedRoute page={["dashboard", "leads", "quotes", "visitors", "tasks", "calls", "customers", "attendance", "invoice-gen", "cashbook", "finance-payments", "inventory", "purchase-orders", "projects"]}><Layout><StartHere /></Layout></ProtectedRoute>} />
          <Route path="/admin/data-check" element={<ProtectedRoute page="data-check"><Layout><DataCheck /></Layout></ProtectedRoute>} />
          <Route path="/admin/business" element={<ProtectedRoute page="business"><Layout><BusinessSettings /></Layout></ProtectedRoute>} />
          <Route path="/admin/master-data" element={<ProtectedRoute page="master-data"><Layout><MasterData /></Layout></ProtectedRoute>} />
          <Route path="/admin/custom-fields" element={<ProtectedRoute page="custom-fields"><Layout><CustomFields /></Layout></ProtectedRoute>} />
          <Route path="/admin/teams" element={<ProtectedRoute page="teams"><Layout><Teams /></Layout></ProtectedRoute>} />
          <Route path="/admin/roles-permissions" element={<ProtectedRoute page="roles-permissions"><Layout><RolesPermissions /></Layout></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute page="customers"><Layout><Customers /></Layout></ProtectedRoute>} />
          <Route path="/customers/:id" element={<ProtectedRoute page="customers"><Layout><CustomerPage /></Layout></ProtectedRoute>} />
          <Route path="/quotes/followups" element={<ProtectedRoute page="quote-followups"><Layout><QuoteFollowups /></Layout></ProtectedRoute>} />
          <Route path="/reports/project-pnl" element={<ProtectedRoute page="project-pnl"><Layout><ProjectPnL /></Layout></ProtectedRoute>} />
          <Route path="/incentives" element={<ProtectedRoute page="incentives"><Layout><Incentives /></Layout></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute page="finance-payments"><Layout><Payments /></Layout></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute page="audit-trail"><Layout><AuditTrail /></Layout></ProtectedRoute>} />
          <Route path="/record-chain" element={<ProtectedRoute page="record-chain"><Layout><RecordChain /></Layout></ProtectedRoute>} />
          {/* Unknown URL (old bookmark, typo) -> home instead of a blank page. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </GuideProvider>
      </BrowserRouter>
      </LostReasonProvider>
      </PrivacyModeProvider>
      </TenantConfigProvider>
    </AuthProvider>
  );
}

export default App;
